import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, HeartHandshake } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Heart as HeartIcon } from "../components/Decor";
import Seo from "../components/Seo";

export default function PaymentResult({ success = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(success ? "checking" : "cancelled");

  useEffect(() => {
    if (!success || !sessionId) return;
    let tries = 0;
    const tick = async () => {
      try {
        const res = await api(`/payments/status/${sessionId}`);
        if (res.payment_status === "paid") { setStatus("paid"); return; }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries < 25) setTimeout(tick, 2000);
      else setStatus("pending_review");
    };
    tick();
  }, [success, sessionId]);

  if (!success) {
    return (
      <div className="bg-candy-mist min-h-screen flex items-center justify-center p-6">
        <Seo title="Checkout Cancelled" noindex />
        <div className="bg-white rounded-cloud shadow-card p-10 max-w-md text-center" data-testid="payment-cancelled">
          <HeartHandshake className="w-14 h-14 text-lilac-accent mx-auto mb-4" />
          <h1 className="font-display font-semibold text-3xl">No worries!</h1>
          <p className="text-muted mt-3 font-semibold">Your checkout was cancelled and nothing was charged. Your cozy bag is safe and waiting.</p>
          <Link to="/cart" data-testid="payment-cancel-back-to-cart" className="mt-6 inline-block px-6 py-3 rounded-full bg-candy text-white font-bold shadow-pink hover:bg-candy-deep transition-colors duration-200">Back to my bag</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-candy-mist min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      {status === "paid" && [...Array(8)].map((_, i) => (
        <HeartIcon key={i} className="absolute text-candy/40 yr-anim" style={{ left: `${8 + i * 11}%`, top: `${10 + (i % 4) * 22}%`, width: 20 + (i % 3) * 12, animation: `yr-bob ${3 + i * 0.4}s ease-in-out infinite` }} />
      ))}
      <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 20 }} className="bg-white rounded-cloud shadow-card p-10 max-w-md text-center relative" data-testid={`payment-${status}`}>
        <Seo title="Order Confirmation" noindex />
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-mint-accent mx-auto mb-4" />
            <h1 data-testid="payment-success-heading" className="font-display font-semibold text-3xl">Hooray — it's yours!</h1>
            <p className="text-muted mt-3 font-semibold">Your order is confirmed and Rachel's workshop is doing a happy dance. A receipt email follows once your mailbox is configured.</p>
          </>
        )}
        {status === "checking" && (
          <>
            <span className="inline-block w-14 h-14 border-4 border-candy-soft border-t-candy rounded-full animate-spin mb-4" />
            <h1 className="font-display font-semibold text-2xl" data-testid="payment-checking-heading">Confirming your payment…</h1>
            <p className="text-muted mt-2 font-semibold">Making sure every stitch is in place.</p>
          </>
        )}
        {status === "pending_review" && (
          <>
            <HeartHandshake className="w-14 h-14 text-butter-accent mx-auto mb-4" />
            <h1 data-testid="payment-pending-heading" className="font-display font-semibold text-2xl">Almost there!</h1>
            <p className="text-muted mt-3 font-semibold">Your payment is still confirming. We'll update your order as soon as Stripe tells us — usually within minutes.</p>
          </>
        )}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/shop" data-testid="payment-success-shop-link" className="px-6 py-3 rounded-full bg-candy text-white font-bold shadow-pink hover:bg-candy-deep transition-colors duration-200">Keep browsing</Link>
          <Link to="/" data-testid="payment-success-home-link" className="px-6 py-3 rounded-full border-2 border-candy-soft font-bold hover:bg-candy-soft transition-colors duration-200">Back home</Link>
        </div>
      </motion.div>
    </div>
  );
}
